import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { Tooltip } from "@heroui/tooltip";
import Map, {
  Marker,
  Source,
  Layer,
  ViewState,
  ViewStateChangeEvent,
  MapLayerMouseEvent,
} from "react-map-gl";
import mapboxgl from "mapbox-gl";
import { TripTransport, TransportLeg, Location } from "@/types/trip";
import "mapbox-gl/dist/mapbox-gl.css";
import * as turf from "@turf/turf";

// Move helper functions outside component to prevent recreation
const formatTimeToHHMM = (isoString: string): string => {
  const date = new Date(isoString);
  let hours = date.getUTCHours();
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${hours}:${minutes} ${ampm}`;
};

const calculateAntimeridianBounds = (
  legs: TransportLeg[] | DefaultLocation[] | undefined
) => {
  if (!legs || legs.length === 0) {
    return new mapboxgl.LngLatBounds([-180, -90], [180, 90]);
  }
  let minLng = 180;
  let maxLng = -180;
  let minLat = 90;
  let maxLat = -90;
  let crossesAntimeridian = false;

  if ("departure" in (legs[0] as TransportLeg)) {
    // Handle TransportLeg array
    (legs as TransportLeg[]).forEach((leg, index) => {
      const fromLng = parseFloat(
        leg.departure.location.coordinates.longitude.toString()
      );
      const fromLat = parseFloat(
        leg.departure.location.coordinates.latitude.toString()
      );
      const toLng = parseFloat(
        leg.arrival.location.coordinates.longitude.toString()
      );
      const toLat = parseFloat(
        leg.arrival.location.coordinates.latitude.toString()
      );

      if (Math.abs(toLng - fromLng) > 180) {
        crossesAntimeridian = true;
      }

      minLat = Math.min(minLat, fromLat, toLat);
      maxLat = Math.max(maxLat, fromLat, toLat);

      if (crossesAntimeridian) {
        if (fromLng < 0) {
          minLng = Math.min(minLng, fromLng);
          maxLng = Math.max(maxLng, toLng);
        } else {
          minLng = Math.min(minLng, toLng);
          maxLng = Math.max(maxLng, fromLng);
        }
      } else {
        minLng = Math.min(minLng, fromLng, toLng);
        maxLng = Math.max(maxLng, fromLng, toLng);
      }
    });
  } else {
    // Handle DefaultLocation array
    const fromLng = parseFloat((legs[0] as DefaultLocation).longitude);
    const fromLat = parseFloat((legs[0] as DefaultLocation).latitude);
    const toLng = parseFloat((legs[1] as DefaultLocation).longitude);
    const toLat = parseFloat((legs[1] as DefaultLocation).latitude);

    if (Math.abs(toLng - fromLng) > 180) {
      crossesAntimeridian = true;
    }

    minLat = Math.min(fromLat, toLat);
    maxLat = Math.max(fromLat, toLat);

    if (crossesAntimeridian) {
      if (fromLng < 0) {
        minLng = Math.min(minLng, fromLng);
        maxLng = Math.max(maxLng, toLng);
      } else {
        minLng = Math.min(minLng, toLng);
        maxLng = Math.max(maxLng, fromLng);
      }
    } else {
      minLng = Math.min(fromLng, toLng);
      maxLng = Math.max(fromLng, toLng);
    }
  }

  if (crossesAntimeridian) {
    if (maxLng - minLng > 180) {
      const temp = minLng;
      minLng = maxLng;
      maxLng = temp + 360;
    }
  }

  return new mapboxgl.LngLatBounds([minLng, minLat], [maxLng, maxLat]);
};

interface DefaultLocation {
  longitude: string;
  latitude: string;
  city: string;
  airport: string;
  timezone: string;
}

interface MapViewProps {
  selectedFlight: TripTransport | null;
  relevantAddedFlight: TripTransport | null;
  fromLocation: Location;
  toLocation: Location;
}

const MapView: React.FC<MapViewProps> = ({
  selectedFlight,
  relevantAddedFlight,
  fromLocation,
  toLocation,
}) => {
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN as string;
  const [isMapReady, setIsMapReady] = useState(false);
  const [selectedLegIndex, setSelectedLegIndex] = useState(0);
  const [viewport, setViewport] = useState<ViewState>({
    longitude: 0,
    latitude: 20,
    zoom: 2,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  });

  const mapRef = useRef<mapboxgl.Map | null>(null);

  // Memoize flightToDisplay to prevent unnecessary recalculations
  const flightToDisplay = useMemo(
    () => selectedFlight || relevantAddedFlight,
    [selectedFlight, relevantAddedFlight]
  );

  // Remove debug logging
  const onMove = useCallback((evt: ViewStateChangeEvent) => {
    setViewport(evt.viewState);
  }, []);

  // Memoize hasValidDefaultLocations calculation
  const hasValidDefaultLocations = useMemo(() => {
    if (!fromLocation || !toLocation) return false;

    const fromLng = parseFloat(fromLocation.coordinates.longitude.toString());
    const fromLat = parseFloat(fromLocation.coordinates.latitude.toString());
    const toLng = parseFloat(toLocation.coordinates.longitude.toString());
    const toLat = parseFloat(toLocation.coordinates.latitude.toString());

    return !isNaN(fromLng) && !isNaN(fromLat) && !isNaN(toLng) && !isNaN(toLat);
  }, [fromLocation, toLocation]);

  // Cleanup map instance and event listeners
  useEffect(() => {
    const currentMap = mapRef.current;

    return () => {
      if (currentMap) {
        // Remove any custom event listeners here if added
        currentMap.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Handle map bounds update with cleanup
  useEffect(() => {
    if (isMapReady && mapRef.current) {
      const map = mapRef.current;
      let bounds;

      if (flightToDisplay?.legs) {
        bounds = calculateAntimeridianBounds(flightToDisplay.legs);
      } else if (fromLocation && toLocation) {
        bounds = calculateAntimeridianBounds([
          {
            longitude: fromLocation.coordinates.longitude.toString(),
            latitude: fromLocation.coordinates.latitude.toString(),
            city: fromLocation.name,
            airport: fromLocation.airports[0] || "",
            timezone: fromLocation.date.time_zone,
          },
          {
            longitude: toLocation.coordinates.longitude.toString(),
            latitude: toLocation.coordinates.latitude.toString(),
            city: toLocation.name,
            airport: toLocation.airports[0] || "",
            timezone: toLocation.date.time_zone,
          },
        ]);
      } else {
        bounds = new mapboxgl.LngLatBounds([-180, -90], [180, 90]);
      }

      const animation = map.fitBounds(bounds, {
        padding: 100,
        duration: 1000,
        maxZoom: 5,
      });

      return () => {
        // Cancel the animation if component unmounts during animation
        if (animation && animation.stop) {
          animation.stop();
        }
      };
    }
  }, [flightToDisplay, isMapReady, fromLocation, toLocation]);

  // Reset selected leg when flight changes
  useEffect(() => {
    if (flightToDisplay?.legs && flightToDisplay.legs.length > 0) {
      setSelectedLegIndex(0);
    }
  }, [flightToDisplay]);

  const handleRouteClick = useCallback((event: MapLayerMouseEvent) => {
    if (event.features && event.features[0]) {
      const legIndex = event.features[0].properties?.legIndex;
      if (typeof legIndex === "number") {
        setSelectedLegIndex(legIndex);
      }
    }
  }, []);

  const handlePreviousLeg = useCallback(() => {
    if (flightToDisplay?.legs) {
      setSelectedLegIndex((prev) =>
        prev > 0 ? prev - 1 : flightToDisplay.legs.length - 1
      );
    }
  }, [flightToDisplay]);

  const handleNextLeg = useCallback(() => {
    if (flightToDisplay?.legs) {
      setSelectedLegIndex((prev) =>
        prev < flightToDisplay.legs.length - 1 ? prev + 1 : 0
      );
    }
  }, [flightToDisplay]);

  // Memoize airline avatar renderer
  const renderAirlineAvatar = useCallback((leg: TransportLeg) => {
    const imageUrl = `https://images.kiwi.com/airlines/128x128/${leg.provider.code}.png?default=airline.png`;
    return (
      <div
        className="rounded-full border-2 border-white shadow-sm bg-white"
        style={{
          width: "2.5rem",
          height: "2.5rem",
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
    );
  }, []);

  // Memoize route features calculation
  const routeFeatures = useMemo(() => {
    if (!flightToDisplay?.legs) return [];

    return flightToDisplay.legs.map((leg, index) => {
      const from = [
        parseFloat(leg.departure.location.coordinates.longitude.toString()),
        parseFloat(leg.departure.location.coordinates.latitude.toString()),
      ];
      const to = [
        parseFloat(leg.arrival.location.coordinates.longitude.toString()),
        parseFloat(leg.arrival.location.coordinates.latitude.toString()),
      ];
      const greatCircleFeature = turf.greatCircle(from, to, { npoints: 100 });
      greatCircleFeature.properties = {
        ...greatCircleFeature.properties,
        legIndex: index,
      };
      return greatCircleFeature;
    });
  }, [flightToDisplay]);

  // Memoize markers rendering
  const renderMarkers = useCallback(() => {
    if (!flightToDisplay) return null;

    return flightToDisplay.legs.map((leg, index) => (
      <React.Fragment key={index}>
        <Marker
          longitude={parseFloat(
            leg.departure.location.coordinates.longitude.toString()
          )}
          latitude={parseFloat(
            leg.departure.location.coordinates.latitude.toString()
          )}
        >
          <Tooltip
            showArrow={true}
            closeDelay={0}
            content={
              <div className="p-1 flex flex-col items-center">
                <h3 className="text-neutral-800 font-semibold text-[11px]">
                  {leg.departure.location.name}
                  {flightToDisplay.type !== "custom" &&
                    ` (${leg.departure.location.code})`}
                </h3>
                {flightToDisplay.type === "api" && leg.departure.time.local && (
                  <p className="text-neutral-600 text-[10px]">
                    Depart at {formatTimeToHHMM(leg.departure.time.local)}
                  </p>
                )}
              </div>
            }
          >
            {index === 0 ? (
              <div className="w-7 h-7 bg-white rounded-full text-neutral-800 flex items-center justify-center shadow-md ring-1 ring-indigo-800">
                <i className="fa-solid fa-plane-departure text-[11px] text-neutral-800"></i>
              </div>
            ) : (
              <div className="w-4 h-4 bg-indigo-300 rounded-full border-2 border-white shadow-md" />
            )}
          </Tooltip>
        </Marker>
        {index === flightToDisplay.legs.length - 1 && (
          <Marker
            longitude={parseFloat(
              leg.arrival.location.coordinates.longitude.toString()
            )}
            latitude={parseFloat(
              leg.arrival.location.coordinates.latitude.toString()
            )}
          >
            <Tooltip
              showArrow={true}
              closeDelay={0}
              content={
                <div className="p-1 flex flex-col items-center">
                  <h3 className="text-neutral-800 font-semibold text-[11px]">
                    {leg.arrival.location.name}
                    {flightToDisplay.type !== "custom" &&
                      ` (${leg.arrival.location.code})`}
                  </h3>
                  {flightToDisplay.type === "api" && leg.arrival.time.local && (
                    <p className="text-neutral-600 text-[10px]">
                      Arrive at {formatTimeToHHMM(leg.arrival.time.local)}
                    </p>
                  )}
                </div>
              }
            >
              <div className="w-7 h-7 bg-white rounded-full text-neutral-800 flex items-center justify-center shadow-md ring-1 ring-indigo-800">
                <i className="fa-solid fa-plane-arrival text-[11px] text-neutral-800"></i>
              </div>
            </Tooltip>
          </Marker>
        )}
      </React.Fragment>
    ));
  }, [flightToDisplay]);

  // Memoize default markers rendering
  const renderDefaultMarkers = useCallback(() => {
    if (!hasValidDefaultLocations || !fromLocation || !toLocation) return null;

    return (
      <>
        <Marker
          longitude={parseFloat(fromLocation.coordinates.longitude.toString())}
          latitude={parseFloat(fromLocation.coordinates.latitude.toString())}
        >
          <Tooltip
            showArrow={true}
            closeDelay={0}
            content={
              <div className="p-1 flex flex-col items-center">
                <h3 className="text-neutral-800 font-semibold text-[11px]">
                  {fromLocation.name}
                </h3>
              </div>
            }
          >
            <div className="w-5 h-5 bg-indigo-700 rounded-full border-2 border-white shadow-md" />
          </Tooltip>
        </Marker>
        <Marker
          longitude={parseFloat(toLocation.coordinates.longitude.toString())}
          latitude={parseFloat(toLocation.coordinates.latitude.toString())}
        >
          <Tooltip
            showArrow={true}
            closeDelay={0}
            content={
              <div className="p-1 flex flex-col items-center">
                <h3 className="text-neutral-800 font-semibold text-[11px]">
                  {toLocation.name}
                </h3>
              </div>
            }
          >
            <div className="w-5 h-5 bg-indigo-700 rounded-full border-2 border-white shadow-md" />
          </Tooltip>
        </Marker>
      </>
    );
  }, [hasValidDefaultLocations, fromLocation, toLocation]);

  return (
    <div className="w-full h-full rounded-md overflow-hidden relative">
      {/* Map Title Overlay */}
      <div className="absolute top-2 left-2 z-10 bg-white/90 backdrop-blur-sm rounded-lg shadow p-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center">
            <i className="fa-solid fa-map text-indigo-600 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-medium text-neutral-900">Route Map</h3>
            <div className="text-xs text-neutral-500 font-light flex items-center gap-2">
              {(() => {
                if (flightToDisplay?.legs && flightToDisplay.legs.length > 0) {
                  const firstLeg = flightToDisplay.legs[0]!;
                  const lastLeg =
                    flightToDisplay.legs[flightToDisplay.legs.length - 1]!;
                  return (
                    <>
                      {firstLeg.departure.location.name} to{" "}
                      {lastLeg.arrival.location.name}
                      <div className="h-1 w-1 bg-indigo-500 rounded-full"></div>
                      {flightToDisplay.legs.length}{" "}
                      {flightToDisplay.legs.length === 1 ? "leg" : "legs"}
                    </>
                  );
                } else if (fromLocation && toLocation) {
                  return `${fromLocation.name} to ${toLocation.name}`;
                }
                return "Select flight to view route";
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Leg Navigation Arrows */}
      {flightToDisplay?.legs && flightToDisplay.legs.length > 1 && (
        <div className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center justify-between w-[95%] pointer-events-none">
          <button
            onClick={handlePreviousLeg}
            className="w-10 h-10 rounded-full bg-white shadow-lg border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-neutral-900 transition-colors pointer-events-auto"
            title="Previous leg"
          >
            <i className="fa-solid fa-chevron-left text-sm"></i>
          </button>
          <button
            onClick={handleNextLeg}
            className="w-10 h-10 rounded-full bg-white shadow-lg border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-neutral-900 transition-colors pointer-events-auto"
            title="Next leg"
          >
            <i className="fa-solid fa-chevron-right text-sm"></i>
          </button>
        </div>
      )}

      <Map
        mapboxAccessToken={mapboxToken}
        ref={(ref) => {
          if (ref) {
            mapRef.current = ref.getMap();
            setIsMapReady(true);
          }
        }}
        {...viewport}
        onMove={onMove}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/jamiedennis8/clxmyy67600fs01pu0d6q2ha7/draft"
        attributionControl={false}
        interactiveLayerIds={["route-lines"]}
        onClick={handleRouteClick}
      >
        {flightToDisplay?.legs && (
          <Source
            id="route"
            type="geojson"
            data={{
              type: "FeatureCollection",
              features: routeFeatures,
            }}
          >
            <Layer
              id="route-lines"
              type="line"
              source="route"
              layout={{
                "line-join": "round",
                "line-cap": "round",
              }}
              paint={{
                "line-color": [
                  "case",
                  ["==", ["get", "legIndex"], selectedLegIndex],
                  "#4337C9",
                  "#818CF8",
                ],
                "line-width": [
                  "case",
                  ["==", ["get", "legIndex"], selectedLegIndex],
                  4,
                  2,
                ],
              }}
            />
            {/* Add a wider invisible line for easier clicking */}
            <Layer
              id="route-lines-hit-area"
              type="line"
              source="route"
              layout={{
                "line-join": "round",
                "line-cap": "round",
              }}
              paint={{
                "line-color": "transparent",
                "line-width": 24,
              }}
            />
          </Source>
        )}
        {flightToDisplay ? renderMarkers() : renderDefaultMarkers()}
      </Map>

      {/* Flight Leg Card Inset */}
      {flightToDisplay?.legs && flightToDisplay.legs[selectedLegIndex] && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-[90%] max-w-2xl">
          <div className="bg-white rounded-lg shadow p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 w-[60%]">
                <div className="flex-shrink-0">
                  {renderAirlineAvatar(flightToDisplay.legs[selectedLegIndex])}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-neutral-900 truncate">
                      {flightToDisplay.legs[selectedLegIndex].provider.name}
                    </span>
                    <span className="text-xs font-medium text-neutral-500 flex-shrink-0">
                      {flightToDisplay.legs[selectedLegIndex].provider.code}{" "}
                      {flightToDisplay.legs[selectedLegIndex].reference}
                    </span>
                  </div>
                  <div className="flex items-center text-xs text-neutral-500 w-full">
                    <div className="flex items-center min-w-0 gap-1">
                      <span className="font-light text-neutral-700 truncate">
                        {
                          flightToDisplay.legs[selectedLegIndex].departure
                            .location.name
                        }
                      </span>
                      <i className="fa-solid fa-arrow-right text-[10px] mx-1 text-neutral-400 flex-shrink-0"></i>
                      <span className="font-light text-neutral-700 truncate">
                        {
                          flightToDisplay.legs[selectedLegIndex].arrival
                            .location.name
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 w-[40%] justify-end">
                {flightToDisplay.type === "api" && (
                  <div className="flex flex-col items-end w-20 flex-shrink-0">
                    <span className="text-xs text-neutral-500">Duration</span>
                    <span className="text-xs font-medium text-neutral-900">
                      {Math.floor(
                        flightToDisplay.legs[selectedLegIndex].duration / 60
                      )}
                      h {flightToDisplay.legs[selectedLegIndex].duration % 60}m
                    </span>
                  </div>
                )}
                {flightToDisplay.legs.length > 1 && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={handlePreviousLeg}
                      className="w-6 h-6 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      <i className="fa-solid fa-chevron-left text-xs"></i>
                    </button>
                    <div className="w-[85px] px-2 py-1 bg-indigo-50 rounded text-xs font-medium text-indigo-600 text-center">
                      {selectedLegIndex + 1} of {flightToDisplay.legs.length}
                    </div>
                    <button
                      onClick={handleNextLeg}
                      className="w-6 h-6 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      <i className="fa-solid fa-chevron-right text-xs"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;
